import React, { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./App.css";
import Forside from "./pages/Forside.jsx";
import { supabase } from "./supabaseClient";
import CookieBanner from './components/CookieBanner.jsx';
import LoadingScreen from "./components/LoadingScreen.jsx";
import { useTheme } from "./ThemeContext.jsx";
import ThemeToggle from "./components/ThemeToggle.jsx";

// CODE SPLITTING: alle sider undtagen forsiden hentes som separate JS-chunks,
// først når en besøgende åbner siden. <Suspense> nedenfor om <Routes> viser
// LoadingScreen, mens en sides chunk hentes.
const Contact = lazy(() => import("./pages/Contact.jsx"));
const Auth = lazy(() => import("./pages/Auth.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.jsx"));
const Metode = lazy(() => import("./pages/Metode.jsx"));
const Udviklere = lazy(() => import("./pages/Udviklere.jsx"));
const TermsData = lazy(() => import("./pages/termsData.jsx"));
const Sustainability = lazy(() => import("./pages/sustainability.jsx"));
const Profile = lazy(() => import('./pages/profile'));
const Billing = lazy(() => import('./pages/Billing'));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword.jsx"));
const About = lazy(() => import("./pages/about.jsx"));
const MineTurerRouter = lazy(() => import("./pages/MineTurerRouter.jsx"));
const MineBiler = lazy(() => import("./pages/MineBiler.jsx"));
const FlaadeDashboardSide = lazy(() => import("./pages/FlaadeDashboardSide.jsx"));
const EnergiOverblik = lazy(() => import("./components/EnergiOverblik"));
const EnergiTyskland = lazy(() => import("./components/EnergiTyskland"));
const EnergiLandRute = lazy(() => import("./components/EnergiLand").then((m) => ({ default: m.EnergiLandRute })));
const MitDashboard = lazy(() => import("./pages/MitDashboard.jsx"));
const StroemAar = lazy(() => import("./pages/StroemAar.jsx"));
const VidenOversigt = lazy(() => import("./pages/Viden.jsx"));
const VidenArtikel = lazy(() => import("./pages/Viden.jsx").then((m) => ({ default: m.VidenArtikel })));
const RouteCo2Calculator = lazy(() => import("./pages/RouteCo2Calculator.jsx"));
const FleetSolution = lazy(() => import("./pages/FleetSolution.jsx"));
const OpsDashboard = lazy(() => import('./pages/OpsDashboard'));

// --- TYSK (DE) ---
// Tysk version af header og footer. Nøglen er den engelske tekst (L(da, en)
// slår op her, når sproget er "de"). Mangler en tekst, vises den engelske.
// Sider uden tysk oversættelse får sproget "en" (se sideSprog).
const TYSK = {
  "Sign Up": "Registrieren",
  "Login": "Anmelden",
  "Sign up": "Registrieren",
  "Profile": "Profil",
  "Dashboard": "Dashboard",
  "Log out": "Abmelden",
  "Method": "Methode",
  "Pricing": "Preise",
  "Knowledge": "Wissen",
  "Privacy & Cookie Policy": "Datenschutz & Cookies",
  "Terms of Use": "Nutzungsbedingungen",
  "About": "Über uns",
  "Contact": "Kontakt",
  "Sustainability": "Nachhaltigkeit",
  "© 2026 OFFLIST, All rights reserved.": "© 2026 OFFLIST, alle Rechte vorbehalten.",
};

function App() {
  const { theme } = useTheme();
  const [session, setSession] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Ny side starter altid i toppen, uanset hvor langt nede man stod. Et link
  // med #anker (fx #modul-alle-ture) hopper i stedet til ankeret.
  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const [language, setLanguage] = useState(() => { try { return localStorage.getItem("offlist_language") || "dk"; } catch { return "dk"; } });
  // L(dansk, engelsk): tysk slås op i TYSK ud fra den engelske tekst.
  const L = (da, en) => (language === "de" ? (TYSK[en] ?? en) : language === "en" ? en : da);
  // Sider uden tysk oversættelse vises på engelsk, når sproget er tysk.
  const sideSprog = language === "de" ? "en" : language;
  // Den tyske energiside (/de/...) slår tysk til, og <html lang> følger sproget.
  useEffect(() => {
    if (location.pathname.startsWith("/de/") && language !== "de") setLanguage("de");
    if (location.pathname.startsWith("/en/") && language !== "en") setLanguage("en");
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.documentElement.lang = language === "dk" ? "da" : language;
    try { localStorage.setItem("offlist_language", language); } catch { /* ingen lagring */ }
  }, [language]);
  // Kontoens sprog (valgt ved oprettelse, gemt i user_metadata.language og
  // spejlet til profiles.language) bruges, når brugeren logger ind. Skifter
  // brugeren sprog på profilen, gemmes det på kontoen.
  useEffect(() => {
    const kontoSprog = session?.user?.user_metadata?.language;
    if (["dk", "en", "de"].includes(kontoSprog) && !/^\/(de|en)\//.test(location.pathname)) setLanguage(kontoSprog);
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const vaelgSprog = (nytSprog) => {
    setLanguage(nytSprog);
    if (session?.user && session.user.user_metadata?.language !== nytSprog) {
      supabase.auth.updateUser({ data: { language: nytSprog } }).catch((e) => console.error("Kunne ikke gemme sprog:", e));
    }
  };

  // Nederst på siden får baggrunden footerens mørke farve (ingen hvid kant ved overscroll).
  useEffect(() => {
    const handleScroll = () => {
      const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200;
      if (isNearBottom) {
        document.documentElement.style.backgroundColor = '#141414';
      } else {
        const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg-color').trim();
        document.documentElement.style.backgroundColor = bg || '#ffffff';
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- SUPABASE SESSION ---
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  const handleShowFrontpage = () => navigate("/");
  const handleShowAbout = () => navigate("/about");
  const handleShowContact = () => navigate("/contact");
  const handleShowSustainability = () => navigate("/sustainability");
  const handleShowPrivacy = () => navigate("/PrivacyPolicy");
  const handleShowSignup = () => navigate("/signup");
  const handleShowTerms = () => navigate("/termsData");
  const handleShowLogin = () => navigate("/login");
  const handleShowProfile = () => navigate("/profile");

  return (
  <div className="page">
    <style>{`
      html, body {
        overflow-x: hidden !important;
        max-width: 100vw;
        margin: 0;
        padding: 0;
      }
      *, *::before, *::after {
        box-sizing: border-box !important;
      }
      @media (max-width: 600px) {
        .auth-btn {
          padding: 8px 10px !important;
          font-size: 0.75rem !important;
        }
        .top-bar-right {
          gap: 4px !important;
        }
      }
    `}</style>
    <CookieBanner onShowPrivacy={() => { navigate("/privacy"); }} />

    {/* --- UNIFIED HEADER WRAPPER --- */}
      <div className="global-header-wrapper">
          
         <header className="sticky-top-bar" style={{ justifyContent: "space-between" }}>
              {/* VENSTRE: Logo */}
              <div className="top-bar-left">
                  <div className="site-logo" onClick={handleShowFrontpage}>
                      <img
                          src="/logos/OFFLIST2.png"
                          alt="OFFLIST Logo"
                          // Logo-filen er sort tekst på hvid baggrund (ingen transparens).
                          // "multiply" lader den hvide baggrund forsvinde ind i siden - men
                          // kun når siden selv er lys. I dark mode inverterer vi i stedet
                          // billedet (sort->hvid, hvid->sort) og bruger "screen", som har
                          // samme "lad baggrunden forsvinde"-effekt, blot modsat vej - så
                          // logoet altid står skarpt, uanset hvor mørk/lys headeren er.
                          style={theme === 'dark'
                              ? { filter: 'invert(1)', mixBlendMode: 'screen' }
                              : { mixBlendMode: 'multiply' }}
                      />
                  </div>
              </div>
              
             {/* INDSÆT DETTE: */}
<div className="top-bar-right">
  <ThemeToggle />
  {!session ? (
    <>
      <button className="auth-btn" onClick={handleShowSignup}>
        {L("Opret", "Sign Up")}
      </button>
      <button className="auth-btn" onClick={handleShowLogin}>
        {L("Log ind", "Login")}
      </button>
    </>
  ) : (
    <>
      <button className="auth-btn" onClick={() => navigate("/mit-dashboard")}>
        {L("Dashboard", "Dashboard")}
      </button>
      <button className="auth-btn" onClick={handleShowProfile}>
        {L("Profil", "Profile")}
      </button>
      <button
        className="auth-btn"
        onClick={async () => {
          await supabase.auth.signOut();
          window.location.href = "/";
        }}
      >
        {L("Log ud", "Log out")}
      </button>
    </>
  )}
</div>
          </header>
      </div>
      <div className="page-layout">
        <main className="main-content">
          <Suspense fallback={<LoadingScreen />}>
          <Routes>
              <Route path="/" element={<Forside language={language} session={session} />} />
              <Route path="/flaadestyring" element={<Forside language={language} session={session} fokus="flaade" />} />
              <Route path="/energi" element={<EnergiOverblik session={session} />} />
              <Route path="/energi/tyskland" element={<EnergiTyskland sprog="da" />} />
              <Route path="/energi/:slug" element={<EnergiLandRute sprog="da" fallback={<NotFound language={sideSprog} />} />} />
              <Route path="/en/electricity-price/:slug" element={<EnergiLandRute sprog="en" fallback={<NotFound language="en" />} />} />
              <Route path="/mit-dashboard" element={<MitDashboard session={session} language={language} />} />
              <Route path="/mit-klimaaftryk" element={<Navigate to="/mit-dashboard" replace />} />
              <Route path="/dit-stroemaar" element={<StroemAar session={session} language={language} />} />
              <Route path="/viden" element={<VidenOversigt />} />
              <Route path="/viden/:slug" element={<VidenArtikel />} />
              <Route path="/en/knowledge" element={<VidenOversigt sprog="en" />} />
              <Route path="/en/knowledge/:slug" element={<VidenArtikel sprog="en" />} />
              <Route path="/de/strompreis" element={<EnergiTyskland sprog="de" />} />
              <Route path="/de/strompreis/:slug" element={<EnergiLandRute sprog="de" fallback={<NotFound language="en" />} />} />
              <Route path="/ops" element={<OpsDashboard />} />
              <Route path="/co2-beregner" element={<RouteCo2Calculator session={session} language={language} />} />
              <Route path="/mine-ture" element={<MineTurerRouter session={session} language={sideSprog} />} />
              <Route path="/mine-biler" element={<MineBiler session={session} language={sideSprog} />} />
              <Route path="/flaade-dashboard" element={<FlaadeDashboardSide session={session} language={sideSprog} />} />

              {/* INFO SIDER */}
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy language={sideSprog} />} />
              <Route path="/metode" element={<Metode language={sideSprog} />} />
              <Route path="/udviklere" element={<Udviklere language={language} session={session} />} />
              <Route path="/contact" element={<Contact language={sideSprog} />} />
              <Route path="/termsdata" element={<TermsData language={sideSprog} />} />
              <Route path="/termsData" element={<TermsData language={sideSprog} />} />
              <Route path="/billing" element={<Billing session={session} language={sideSprog} />} />
              <Route path="/erhverv" element={<FleetSolution language={language} />} />
              <Route path="/update-password" element={<UpdatePassword language={sideSprog} />} />
              <Route path="/sustainability" element={<Sustainability language={sideSprog} />} />
              <Route path="/about" element={<About language={sideSprog} />} />

              {/* AUTH (Login/Signup) */}
              <Route path="/login" element={<Auth view="login" language={language} />} />
              <Route path="/signup" element={<Auth view="signup" language={language} />} />

              <Route path="/profile" element={
                   <Profile
                       session={session}
                       language={language}
                       onLanguageChange={vaelgSprog}
                       onShowSignup={handleShowSignup}
                       onShowLogin={handleShowLogin}
                   />
              } />

              <Route path="*" element={<NotFound language={sideSprog} />} />
          </Routes>
          </Suspense>
        </main>
      </div>

{/* --- GLOBAL BASIC FOOTER --- */}
<footer style={{ 
    backgroundColor: '#141414', 
    color: '#a3a3a3', 
    padding: '40px 20px', 
    marginTop: 'auto', 
    borderTop: '1px solid #2a2a2a', 
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: '0.9rem'
}}>
    {/* To kolonner på én linje (links · juridisk), som først brydes om på
        smalle skærme - se .site-footer-raekke i App.css. Valuta og sprog
        vælges på profilen (pages/profile.jsx). */}
    <div className="site-footer-raekke">
        {/* Venstre side: Links */}
        <div className="site-footer-links" style={{ display: 'flex', gap: '8px 20px', flexWrap: 'wrap' }}>
            <a href="#" style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); handleShowAbout(); }}>{L("About", "About")}</a>
            <a href="#" style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); handleShowContact(); }}>{L("Contact", "Contact")}</a>
            <a href="#" style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); handleShowSustainability(); }}>{L("Sustainability", "Sustainability")}</a>
            <a href="/metode" style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); navigate("/metode"); }}>{L('Metode', 'Method')}</a>
            <a href="/udviklere" style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); navigate("/udviklere"); }}>{L('API', 'API')}</a>
            <a href={language === "dk" ? "/viden" : "/en/knowledge"} style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); navigate(language === "dk" ? "/viden" : "/en/knowledge"); }}>{L('Viden', 'Knowledge')}</a>
            {/* /billing er offentlig (ingen login kraevet) og viser nu
                baade privat- og erhvervspriser via faneskift - se
                Billing.jsx */}
            <a href="#" style={{ color: '#e5e5e5', textDecoration: 'none', fontWeight: '500' }} onClick={(e) => { e.preventDefault(); navigate("/billing"); }}>
                {L('Priser', 'Pricing')}
            </a>
        </div>

        {/* HÃ¸jre side: Juridisk tekst */}
        <div className="site-footer-juridisk" style={{ textAlign: 'right', fontSize: '0.8rem', lineHeight: '1.5', color: '#a3a3a3' }}>
            <p style={{ margin: '0', fontWeight: '500', color: '#e5e5e5' }}>{L("© 2026 OFFLIST, All rights reserved.", "© 2026 OFFLIST, All rights reserved.")}</p>
            <p style={{ cursor: 'pointer', textDecoration: 'underline', margin: '5px 0 0 0', display: 'block' }} onClick={(e) => { e.preventDefault(); handleShowPrivacy(); }}>
                {L('Privatlivs- & Cookiepolitik', 'Privacy & Cookie Policy')}
            </p>
            <p style={{ cursor: 'pointer', textDecoration: 'underline', margin: '2px 0 0 0', display: 'block' }} onClick={(e) => { e.preventDefault(); handleShowTerms(); }}>
                {L('Brugervilkår', 'Terms of Use')}
            </p>
        </div>
    </div>
</footer>
   </div>
  );
}

export default App;
